import { useState, useEffect, useCallback } from 'react';
import i18n from '../../i18n';
import { timetrackingService } from '../utils/serviceProvider';

const mapApiDataToAttendance = (entry) => {
  return {
    employeeId: entry.employee_id,
    employeeName: entry.employee_name,
    date: entry.date,
    checkIn: entry.check_in,
    checkOut: entry.check_out,
    totalHours: entry.total_hours,
    status: entry.status,
    statusLabel: entry.status_label,
    statusColor: entry.status_color,
    notes: entry.notes
  };
};

const calculateMonthlyStats = (entries, month, year) => {
  const stats = {};
  
  entries.forEach(entry => {
    const empId = entry.employeeId;
    if (!stats[empId]) {
      stats[empId] = {
        employeeId: empId,
        employeeName: entry.employeeName,
        nbrJoursMois: 0,
        nbrJoursOuvrables: 0,
        nbrJoursTravailles: 0,
        nbrJoursSupplementaires: 0,
        nbrHeuresSupplementaires: 0,
        nbrJoursAbsence: 0,
        nbrJoursConges: 0,
        periodeTravail: '',
        periodeCongé: '',
        entries: []
      };
    }
    
    stats[empId].entries.push(entry);
    
    if (entry.status === 'P' || entry.status === 'P/2') {
      stats[empId].nbrJoursTravailles += 1;
    } else if (entry.status?.startsWith('C')) {
      stats[empId].nbrJoursConges += 1;
    } else if (entry.status === 'AN' || entry.status === 'AJ' || entry.status === 'AI') {
      stats[empId].nbrJoursAbsence += 1;
    }
    
    if (entry.totalHours) {
      const [hours] = entry.totalHours.split(':').map(Number);
      if (hours > 8) {
        stats[empId].nbrHeuresSupplementaires += (hours - 8);
        stats[empId].nbrJoursSupplementaires = Math.floor(stats[empId].nbrHeuresSupplementaires / 8);
      }
    }
  });
  
  Object.values(stats).forEach(stat => {
    const daysInMonth = new Date(year, month - 1, 0).getDate();
    stat.nbrJoursMois = daysInMonth;
    stat.nbrJoursOuvrables = Math.ceil(daysInMonth * 22 / 30);
    
    if (stat.entries.length > 0) {
      const dates = stat.entries.map(e => e.date).sort();
      stat.periodeTravail = `${dates[0]} - ${dates[dates.length - 1]}`;
    }
  });
  
  return Object.values(stats);
};

export const useAttendanceData = (month, year) => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshAttendance = useCallback(async () => {
    if (!month || !year) {
      setAttendanceData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await timetrackingService.getMonthlyEntries({ month, year });
      
      if (response && Array.isArray(response)) {
        const mappedData = response.map(mapApiDataToAttendance);
        const statsData = calculateMonthlyStats(mappedData, month, year);
        setAttendanceData(statsData);
      } else {
        setAttendanceData([]);
      }
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setError(err.message || i18n.t('common.fetch_attendance_failed'));
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    refreshAttendance();
  }, [refreshAttendance]);

  return {
    attendanceData,
    loading,
    error,
    refresh: refreshAttendance
  };
};

